import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  getCart,
  addToCart as addToCartAPI,
  updateCartItem,
  removeFromCart,
} from "../../services/Cart/CartServices";
import { getProductDetail } from "../../services/Products/ProductServices";
import useConsentStore from "../Consent/useConsentStore";
import { translateProductName } from "../../utils/translateProductName";

// ─── Guest storage keys ──────────────────────────────────────────────────────
const GUEST_CART_KEY = "lianne_guest_cart";

// ─── Guest helpers ───────────────────────────────────────────────────────────
const getGuestStorage = () => useConsentStore.getState().getStorage();

const normalizeAttributes = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object") return {};

  return Object.keys(attributes)
    .sort()
    .reduce((result, key) => {
      const value = attributes[key];
      if (value !== undefined && value !== null && value !== "") {
        result[key] = String(value);
      }
      return result;
    }, {});
};

const buildGuestLocalKey = (productId, variationId = 0, attributes = {}) => {
  const normalizedAttributes = normalizeAttributes(attributes);
  const attributeQuery = Object.keys(normalizedAttributes)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          normalizedAttributes[key]
        )}`
    )
    .join("&");

  return `${Number(productId)}:${Number(variationId || 0)}:${attributeQuery}`;
};

const normalizeGuestCartItem = (item) => {
  const productId = Number(item.product_id || item.id);
  const variationId = Number(item.variation_id || 0);
  const attributes = normalizeAttributes(item.attributes || {});
  const localKey =
    item.local_key || buildGuestLocalKey(productId, variationId, attributes);
  const quantity = Math.max(1, parseInt(item.quantity, 10) || 1);
  const price = Number(item.price || item.display?.price || 0);

  return {
    ...item,
    id: productId,
    product_id: productId,
    variation_id: variationId || null,
    attributes,
    local_key: localKey,
    cart_item_key: item.cart_item_key || localKey,
    name: item.name || item.display?.name || `Product ${productId}`,
    price,
    quantity,
    total: price * quantity,
    image: item.image || item.display?.image || "",
    selected: item.selected || item.display?.selected_options || "",
    checkout_source: item.checkout_source || "cart",
  };
};

const normalizeGuestCartItems = (items) => {
  const itemMap = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const normalizedItem = normalizeGuestCartItem(item);
    const key = normalizedItem.local_key;
    const existing = itemMap.get(key);

    if (existing) {
      const quantity = existing.quantity + normalizedItem.quantity;
      itemMap.set(key, {
        ...existing,
        ...normalizedItem,
        quantity,
        total: normalizedItem.price * quantity,
      });
    } else {
      itemMap.set(key, normalizedItem);
    }
  });

  return Array.from(itemMap.values());
};

const needsDisplayHydration = (item) =>
  !item.name ||
  item.name === `Product ${item.product_id || item.id}` ||
  !item.image ||
  Number(item.price || 0) <= 0;

const hydrateGuestCartDisplay = async (items) => {
  const hydratedItems = await Promise.all(
    items.map(async (item) => {
      if (!needsDisplayHydration(item)) return item;

      try {
        const product = await getProductDetail(item.product_id || item.id);
        const price = Number(product?.sale_price || product?.price || item.price || 0);

        return normalizeGuestCartItem({
          ...item,
          name: product?.name || item.name,
          price,
          image: product?.images?.[0]?.src || item.image,
          category: product?.categories?.[0]?.slug || item.category,
        });
      } catch {
        return item;
      }
    })
  );

  return normalizeGuestCartItems(hydratedItems);
};

const readGuestCart = () => {
  try {
    const raw = getGuestStorage().getItem(GUEST_CART_KEY);
    return raw ? normalizeGuestCartItems(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (items) => {
  try {
    getGuestStorage().setItem(
      GUEST_CART_KEY,
      JSON.stringify(normalizeGuestCartItems(items))
    );
  } catch {}
};

const clearGuestCartStorage = () => {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
    sessionStorage.removeItem(GUEST_CART_KEY);
  } catch {}
};

const matchesCartIdentifier = (item, identifier) => {
  const id = String(identifier);
  return (
    String(item.local_key) === id ||
    String(item.cart_item_key) === id ||
    String(item.id) === id ||
    String(item.product_id) === id
  );
};

const clearGuestCartItems = (keys = []) => {
  const keySet = new Set((keys || []).map((key) => String(key)));
  if (keySet.size === 0) return readGuestCart();

  const updated = readGuestCart().filter((item) => {
    const possibleKeys = [
      item.local_key,
      item.cart_item_key,
      item.id,
      item.product_id,
    ].map((key) => String(key));

    return !possibleKeys.some((key) => keySet.has(key));
  });

  writeGuestCart(updated);
  return updated;
};

const clearGuestCartItemsByIndexes = (indexes = []) => {
  const indexSet = new Set((indexes || []).map((index) => Number(index)));
  if (indexSet.size === 0) return readGuestCart();

  const updated = readGuestCart().filter((_, index) => !indexSet.has(index));
  writeGuestCart(updated);
  return updated;
};

/**
 * Tổng hợp cartItems + cartTotals từ mảng items
 */
const computeTotals = (items) => ({
  total: items.reduce((sum, i) => sum + Number(i.price || 0) * i.quantity, 0),
  item_count: items.reduce((sum, i) => sum + Number(i.quantity || 0), 0),
});

// ─── Transform API response → store item (logged-in) ────────────────────────
const transformedItemCart = (item) => ({
  id: item.product_id,
  cart_item_key: item.cart_item_key,
  name: translateProductName(item.name.split(" - ")[0]),
  price: parseFloat(item.price),
  quantity: item.quantity,
  total: item.subtotal,
  image: item.images.full,
  selected:
    item.variation_attributes?.option ||
    item.variation_attributes?.size ||
    item.attributes.option.options[0],
  attributes: item.attributes,
  variation_id: item.variation_id,
  category: item.categories?.[0]?.slug,
  checkout_source: "cart",
  // logged-in items cần product_id cho checkout compatibility
  product_id: item.product_id,
});

// ─── Store ───────────────────────────────────────────────────────────────────
const useCartStore = create(
  persist(
    (set, get) => ({
      // ── State ──────────────────────────────────────────────────────────────
      cartItems: [],
      cartTotals: { total: 0, item_count: 0 },
      isLoading: false,
      error: null,
      selectedCartItemIds: [],

      /**
       * isGuestMode: true  → dùng guest storage (localStorage/sessionStorage)
       * isGuestMode: false → dùng backend cart API (logged-in)
       */
      isGuestMode: true,

      // ── Mode setter (gọi từ DefaultLayout sau auth check) ─────────────────
      setGuestMode: (isGuest) => set({ isGuestMode: isGuest }),

      // ── transformedItemCart (giữ tương thích) ─────────────────────────────
      transformedItemCart,

      // ── showCart ──────────────────────────────────────────────────────────
      showCart: async () => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          // Đợi consent hydrate
          await useConsentStore.getState().waitForHydration();
          const items = await hydrateGuestCartDisplay(readGuestCart());
          writeGuestCart(items);
          set({
            cartItems: items,
            cartTotals: computeTotals(items),
            isLoading: false,
            error: null,
          });
          return;
        }

        // Logged-in: gọi API
        set({ isLoading: true, error: null });
        try {
          const response = await getCart();
          if (response && response.items) {
            const transformedItems = response.items.map(transformedItemCart);
            set({
              cartItems: transformedItems,
              cartTotals: {
                total: parseFloat(response.total),
                item_count: response.item_count,
              },
              isLoading: false,
              error: null,
            });
          } else {
            set({
              cartItems: [],
              cartTotals: { total: 0, item_count: 0 },
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          set({
            error: error.message || "Có lỗi xảy ra khi tải giỏ hàng",
            isLoading: false,
            cartItems: [],
            cartTotals: { total: 0, item_count: 0 },
          });
        }
      },

      // ── addToCart ─────────────────────────────────────────────────────────
      addToCart: async (
        productId,
        quantity = 1,
        variationId = null,
        attributes = null,
        productInfo = null // { name, price, image, selected, category }
      ) => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          const current = readGuestCart();

          const localKey = buildGuestLocalKey(
            productId,
            variationId,
            attributes || {}
          );
          const existingIdx = current.findIndex(
            (item) => item.local_key === localKey
          );

          let updated;
          if (existingIdx >= 0) {
            // Cộng quantity
            updated = current.map((item, idx) =>
              idx === existingIdx
                ? {
                    ...item,
                    quantity: item.quantity + quantity,
                    total: item.price * (item.quantity + quantity),
                  }
                : item
            );
          } else {
            // Thêm mới — cần productInfo để hiển thị
            const newItem = normalizeGuestCartItem({
              id: productId,
              product_id: productId,
              local_key: localKey,
              cart_item_key: localKey,
              name: productInfo?.name || `Product ${productId}`,
              price: productInfo?.price || 0,
              quantity,
              total: (productInfo?.price || 0) * quantity,
              image: productInfo?.image || "",
              selected: productInfo?.selected || "",
              attributes: attributes || {},
              variation_id: variationId,
              category: productInfo?.category || "",
              checkout_source: "cart",
              added_at: Date.now(),
            });
            updated = [...current, newItem];
          }

          writeGuestCart(updated);
          set({
            cartItems: updated,
            cartTotals: computeTotals(updated),
            error: null,
          });
          return { success: true, message: "Đã thêm vào giỏ hàng" };
        }

        // Logged-in: gọi API
        try {
          const response = await addToCartAPI({
            product_id: productId,
            quantity,
            ...(variationId && { variation_id: variationId }),
            ...(attributes && { attributes }),
          });

          if (response && response.items) {
            const transformedItems = response.items.map(transformedItemCart);
            set({
              cartItems: transformedItems,
              cartTotals: {
                total: parseFloat(response.total),
                item_count: response.item_count,
              },
              error: null,
            });
            return { success: true, message: response.message };
          } else {
            return response.message || "Không thể thêm sản phẩm vào giỏ hàng";
          }
        } catch (error) {
          set({ error: error.message || "Có lỗi xảy ra khi thêm sản phẩm" });
          return { success: false, error: error.message };
        }
      },

      // ── updateQuantity ────────────────────────────────────────────────────
      updateQuantity: async (identifier, newQuantity) => {
        if (newQuantity <= 0) return await get().removeFromCart(identifier);

        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          const current = readGuestCart();
          const updated = current.map((item) =>
            matchesCartIdentifier(item, identifier)
              ? {
                  ...item,
                  quantity: newQuantity,
                  total: Number(item.price || 0) * newQuantity,
                }
              : item
          );
          writeGuestCart(updated);
          set({ cartItems: updated, cartTotals: computeTotals(updated) });
          return { success: true, message: "Đã cập nhật số lượng" };
        }

        // Logged-in
        const currentCart = get().cartItems;
        const cartItem = currentCart.find((item) =>
          matchesCartIdentifier(item, identifier)
        );
        if (!cartItem) {
          return { success: false, error: "Không tìm thấy sản phẩm trong giỏ hàng" };
        }

        try {
          const response = await updateCartItem({
            cart_item_key: cartItem.cart_item_key,
            quantity: newQuantity,
          });
          if (response && response.items) {
            const transformedItems = response.items.map(transformedItemCart);
            set({
              cartItems: transformedItems,
              cartTotals: {
                total: parseFloat(response.total),
                item_count: response.item_count,
              },
              isLoading: false,
              error: null,
            });
            return { success: true, message: "Đã cập nhật số lượng sản phẩm" };
          } else {
            throw new Error("Không thể cập nhật số lượng sản phẩm");
          }
        } catch (error) {
          set({ error: error.message || "Có lỗi xảy ra khi cập nhật" });
          return { success: false, error: error.message };
        }
      },

      // ── removeFromCart ────────────────────────────────────────────────────
      removeFromCart: async (identifier) => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          const current = readGuestCart();
          const updated = current.filter(
            (item) => !matchesCartIdentifier(item, identifier)
          );
          writeGuestCart(updated);
          set({ cartItems: updated, cartTotals: computeTotals(updated) });
          return { success: true, message: "Đã xóa sản phẩm khỏi giỏ hàng" };
        }

        // Logged-in
        const currentCart = get().cartItems;
        const cartItem = currentCart.find((item) =>
          matchesCartIdentifier(item, identifier)
        );
        if (!cartItem) {
          return { success: false, error: "Không tìm thấy sản phẩm trong giỏ hàng" };
        }

        try {
          const response = await removeFromCart({ cart_item_key: cartItem.cart_item_key });
          if (response && response.items) {
            const transformedItems = response.items.map(transformedItemCart);
            set({
              cartItems: transformedItems,
              cartTotals: {
                total: parseFloat(response.total),
                item_count: response.item_count,
              },
              isLoading: false,
              error: null,
            });
            return { success: true, message: "Đã xóa sản phẩm khỏi giỏ hàng" };
          } else {
            throw new Error("Không thể xóa sản phẩm khỏi giỏ hàng");
          }
        } catch (error) {
          set({ error: error.message || "Có lỗi xảy ra khi xóa sản phẩm" });
          return { success: false, error: error.message };
        }
      },

      // ── isInCart ──────────────────────────────────────────────────────────
      isInCart: (productId) =>
        get().cartItems.some((item) => item.id === productId),

      getItemById: (productId) =>
        get().cartItems.find((item) => item.id === productId),

      getItemQuantity: (productId) => {
        const item = get().getItemById(productId);
        return item ? item.quantity : 0;
      },

      clearError: () => set({ error: null }),

      // ── reset ─────────────────────────────────────────────────────────────
      reset: () =>
        set({
          cartItems: [],
          cartTotals: { total: 0, item_count: 0 },
          isLoading: false,
          error: null,
        }),

      // ── clearCart ─────────────────────────────────────────────────────────
      /**
       * Clear cart items.
       * Guest: xoá cả storage. Logged-in: reset state (server là source of truth).
       */
      clearCart: () => {
        const { isGuestMode } = get();
        if (isGuestMode) {
          clearGuestCartStorage();
        }
        set({
          cartItems: [],
          cartTotals: { total: 0, item_count: 0 },
          error: null,
        });
      },

      // ── Guest-specific: đọc raw guest cart cho merge ──────────────────────
      _readGuestCart: readGuestCart,
      _clearGuestCartStorage: clearGuestCartStorage,
      _clearGuestCartItems: (keys) => {
        const updated = clearGuestCartItems(keys);
        set({ cartItems: updated, cartTotals: computeTotals(updated) });
        return updated;
      },
      _clearGuestCartItemsByIndexes: (indexes) => {
        const updated = clearGuestCartItemsByIndexes(indexes);
        set({ cartItems: updated, cartTotals: computeTotals(updated) });
        return updated;
      },
      _writeGuestCart: (items) => {
        const updated = normalizeGuestCartItems(items);
        writeGuestCart(updated);
        set({ cartItems: updated, cartTotals: computeTotals(updated) });
      },
      buildGuestLocalKey,

      // ── Getters ───────────────────────────────────────────────────────────
      getTotalItems: () =>
        get().cartItems.reduce((sum, item) => sum + item.quantity, 0),
      getCartItems: () => get().cartItems,
      getCartTotals: () => get().cartTotals,
      isEmpty: () => get().cartItems.length === 0,
    }),
    {
      name: "cart-store",
      // Persist chỉ dùng cho logged-in session state (selectedIds, v.v.)
      // Guest cart data tự quản lý qua consent-aware storage
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedCartItemIds: state.selectedCartItemIds,
        // Không persist cartItems ở đây vì guest dùng riêng, logged-in dùng API
      }),
    }
  )
);

export default useCartStore;
